/**
 * The Watchlist's picture (roadmap D163): how an entry moves, drawn in the
 * app's art language like the Projects view's hero (projects/ProjectModelArt.tsx)
 * — a toned `card-art` with the dots motif, glowing nodes, soft lines and
 * dots drifting along them (still under reduced motion).
 *
 * Three lanes: a risk (Identified → Assessed → Treating → Monitoring →
 * Settled) with the treatment branch over it (Assign → the task with an
 * agent or a team → rate it again) and its four ways to settle, the one
 * that happened falling to the issue lane; an issue (Open → Working →
 * Resolved); a bug (Open → Fixing → Fixed, or won't fix, or duplicate).
 * Each step carries how many entries are at it.
 *
 * Every step, branch point and outcome is a focusable button: it opens an
 * explainer — what it means, who does it (the owner, or the team or agent
 * with the task), what moves it on — with "Show me", which filters the list
 * to the entries at that step. "How the Watchlist works" opens the same
 * dialog on the whole lifecycle. The words are ./riskWorkflowModel.ts's.
 */
import { Button, Modal } from '@heroui/react';
import { type KeyboardEvent, type ReactNode, useMemo, useState } from 'react';

import type { RiskKind } from '@aioe/shared';
import type { RiskRow } from './risksModel.ts';
import { explain, type Explainer, HOW_IT_WORKS, laneSteps, type MapPoint, OWNER_AND_WORK, settleOptions, type StageId, stageOf } from './riskWorkflowModel.ts';

/** The lanes' tones, as the register's counts wear them: risks amber, issues rose, bugs violet. */
const LANE_TONE: Record<RiskKind, string> = { risk: 'oklch(0.76 0.15 75)', issue: 'oklch(0.66 0.18 10)', bug: 'oklch(0.62 0.17 300)' };
const LANE_WORD: Record<RiskKind, string> = { risk: 'Risk', issue: 'Issue', bug: 'Bug' };

const X0 = 96;
const STEP_X: Record<RiskKind, number[]> = { risk: [96, 196, 296, 396, 496], issue: [96, 296, 496], bug: [96, 296, 496] };
const LANE_Y: Record<RiskKind, number> = { risk: 100, issue: 176, bug: 230 };
const OUT_X = 584;

export interface StageFilter {
  kind: RiskKind;
  stage: StageId;
  label: string;
}

type Opened = { point: MapPoint } | { overview: true };

function Point({ x, y, r, tone, label, count, onOpen, children }: { x: number; y: number; r: number; tone: string; label: string; count?: number; onOpen: () => void; children?: ReactNode }) {
  const onKey = (e: KeyboardEvent<SVGGElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };
  return (
    <g className="wl-map__point" role="button" tabIndex={0} aria-label={label} onClick={onOpen} onKeyDown={onKey} style={{ ['--lane' as string]: tone }}>
      <circle cx={x} cy={y} r={r + 7} className="wl-map__halo" />
      <circle cx={x} cy={y} r={r} className="wl-map__node" />
      <circle cx={x} cy={y} r={r + 3} className="wl-map__ring" />
      {count ? (
        <text x={x} y={y + 3.2} className="wl-map__count" textAnchor="middle">
          {count}
        </text>
      ) : null}
      {children}
    </g>
  );
}

function Artwork({ counts, onOpen }: { counts: Map<string, number>; onOpen: (p: MapPoint) => void }) {
  const lanes: RiskKind[] = ['risk', 'issue', 'bug'];
  const branch = { assign: { x: 238, y: 44 }, task: { x: 296, y: 30 }, reassess: { x: 354, y: 44 } };
  const branchPath = `M196 91 C 204 58, 220 44, ${branch.assign.x} 44 S 280 30 ${branch.task.x} 30 S 338 44 ${branch.reassess.x} 44 C 372 44, 388 58, 396 91`;
  return (
    <svg viewBox="0 0 720 262" className="wl-map__svg" role="group" aria-label="How an entry moves on the Watchlist">
      <defs>
        {lanes.map((k) => (
          <linearGradient key={k} id={`wl-line-${k}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor={LANE_TONE[k]} stopOpacity="0.25" />
            <stop offset="0.5" stopColor={LANE_TONE[k]} stopOpacity="0.7" />
            <stop offset="1" stopColor={LANE_TONE[k]} stopOpacity="0.3" />
          </linearGradient>
        ))}
      </defs>

      {/* the treatment branch over the risk lane */}
      <path d={branchPath} className="wl-map__branch" style={{ ['--lane' as string]: LANE_TONE.risk }} />
      <circle r="2.4" className="wl-map__traveller" style={{ ['--lane' as string]: LANE_TONE.risk }}>
        <animateMotion dur="4.4s" repeatCount="indefinite" path={branchPath} />
      </circle>

      {/* "it happened": a risk becomes an issue */}
      <path d={`M${OUT_X - 4} 112 C 560 150, 150 140, ${X0 + 4} ${LANE_Y.issue - 10}`} className="wl-map__happened" />
      <text x={330} y={146} className="wl-map__note" textAnchor="middle">
        it happened → raise an issue
      </text>

      {lanes.map((kind) => {
        const y = LANE_Y[kind];
        const xs = STEP_X[kind];
        const steps = laneSteps(kind);
        const lane = `M${xs[0]} ${y} L ${xs[xs.length - 1]} ${y}`;
        const outcomes = settleOptions(kind);
        const last = xs[xs.length - 1];
        const fan = outcomes.map((o, i) => ({ o, y: y + (i - (outcomes.length - 1) / 2) * (kind === 'risk' ? 20 : 17) }));
        return (
          <g key={kind}>
            <text x={12} y={y + 4} className="wl-map__lane" style={{ ['--lane' as string]: LANE_TONE[kind] }}>
              {LANE_WORD[kind]}
            </text>
            <path d={lane} stroke={`url(#wl-line-${kind})`} className="wl-map__line" />
            <circle r="2.2" className="wl-map__traveller" style={{ ['--lane' as string]: LANE_TONE[kind] }}>
              <animateMotion dur={`${kind === 'risk' ? 5.2 : 3.8}s`} begin={kind === 'bug' ? '1.2s' : '0s'} repeatCount="indefinite" path={lane} />
            </circle>
            {fan.map(({ o, y: oy }) => (
              <path key={o.id} d={`M${last + 10} ${y} C ${last + 44} ${y}, ${OUT_X - 34} ${oy}, ${OUT_X - 6} ${oy}`} className="wl-map__fan" style={{ ['--lane' as string]: LANE_TONE[kind] }} />
            ))}
            {steps.map((s, i) => {
              const n = counts.get(`${kind}:${s.id}`) ?? 0;
              return (
                <Point key={s.id} x={xs[i]} y={y} r={9} tone={LANE_TONE[kind]} count={n} label={`${LANE_WORD[kind]}: ${s.label}, ${n === 1 ? 'one entry' : `${n || 'no'} entries`} here. What it means`} onOpen={() => onOpen({ kind, stage: s.id })}>
                  <text x={xs[i]} y={y + 24} className="wl-map__label" textAnchor="middle">
                    {s.label}
                  </text>
                </Point>
              );
            })}
            {fan.map(({ o, y: oy }) => (
              <Point key={o.id} x={OUT_X} y={oy} r={4} tone={LANE_TONE[kind]} label={`${LANE_WORD[kind]} settled as ${o.label.toLowerCase()}. What it means`} onOpen={() => onOpen({ kind, outcome: o.id })}>
                <text x={OUT_X + 10} y={oy + 3.5} className="wl-map__small">
                  {o.label}
                </text>
              </Point>
            ))}
          </g>
        );
      })}

      {(
        [
          ['assign', 'Assign'],
          ['task', 'Agent or team task'],
          ['reassess', 'Rate again'],
        ] as const
      ).map(([id, words]) => (
        <Point key={id} x={branch[id].x} y={branch[id].y} r={5} tone={LANE_TONE.risk} label={`${words}: part of treating a risk. What it means`} onOpen={() => onOpen({ kind: 'risk', branch: id })}>
          <text x={branch[id].x} y={branch[id].y - 11} className="wl-map__small" textAnchor="middle">
            {words}
          </text>
        </Point>
      ))}
    </svg>
  );
}

function Section({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-foreground">{heading}</span>
      <p className="m-0 text-xs leading-relaxed text-muted">{children}</p>
    </div>
  );
}

const EXPANDED_KEY = 'mojoup.watchlist.mapExpanded';
const SETTLED = new Set<StageId>(['settled', 'resolved', 'fixed']);

/** Collapsed to a one-line tile unless the person opened it before. */
function readExpanded(): boolean {
  try {
    return window.localStorage.getItem(EXPANDED_KEY) === '1';
  } catch {
    return false;
  }
}

export function WatchlistMap({ rows, tone, onShowMe }: { rows: readonly RiskRow[]; tone: string; onShowMe: (f: StageFilter) => void }) {
  const [opened, setOpened] = useState<Opened | null>(null);
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      const key = `${r.entry.kind}:${stageOf(r.entry)}`;
      m.set(key, (m.get(key) ?? 0) + 1);
    }
    return m;
  }, [rows]);
  const ex: Explainer | undefined = opened && 'point' in opened ? explain(opened.point) : undefined;
  const here = ex?.showMe ? (counts.get(`${ex.showMe.kind}:${ex.showMe.stage}`) ?? 0) : 0;
  const [expanded, setExpanded] = useState(readExpanded);
  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    try {
      window.localStorage.setItem(EXPANDED_KEY, next ? '1' : '0');
    } catch {
      // Storage refused (private window): the choice lasts this visit only.
    }
  };
  const open = rows.filter((r) => !SETTLED.has(stageOf(r.entry))).length;
  return (
    <section
      className={`card-art wl-map${expanded ? '' : ' wl-map--tile'}`}
      data-tone={tone}
      data-motif="dots"
      aria-label="How an entry moves"
      data-watchlist-map
      data-expanded={expanded ? 'true' : 'false'}
    >
      {expanded ? (
        <>
          <div className="wl-map__words">
            <h2 className="wl-map__title">How an entry moves</h2>
            <p className="wl-map__blurb">A risk is rated, treated by a team or an agent, watched and settled. An issue and a bug are worked on and settled. Press a step to see what it means and who does it.</p>
            <div className="wl-map__cues">
              <button type="button" className="wl-map__cue" onClick={() => setOpened({ overview: true })} data-risk-help>
                How the Watchlist works
              </button>
              <button type="button" className="wl-map__cue wl-map__cue--quiet" onClick={toggle} aria-expanded="true" data-watchlist-map-toggle>
                Hide the picture
              </button>
            </div>
          </div>
          <Artwork counts={counts} onOpen={(point) => setOpened({ point })} />
        </>
      ) : (
        <>
          <svg viewBox="0 0 36 24" className="wl-map__glyph" aria-hidden="true">
            <path d="M4 5 H32 M4 12 H32 M4 19 H32" />
            <circle cx="4" cy="5" r="2.2" /><circle cx="18" cy="5" r="2.2" /><circle cx="32" cy="5" r="2.2" />
            <circle cx="4" cy="12" r="2.2" /><circle cx="32" cy="12" r="2.2" />
            <circle cx="4" cy="19" r="2.2" /><circle cx="32" cy="19" r="2.2" />
          </svg>
          <div className="wl-map__tile-words">
            <span className="wl-map__title">How an entry moves</span>
            <span className="wl-map__blurb">Identified → Assessed → Treating → Monitoring → Settled{open > 0 ? ` · ${open} in hand` : ''}</span>
          </div>
          <button type="button" className="wl-map__cue" onClick={() => setOpened({ overview: true })} data-risk-help>
            How it works
          </button>
          <button type="button" className="wl-map__expand" onClick={toggle} aria-expanded="false" data-watchlist-map-toggle>
            Show the picture
          </button>
        </>
      )}

      {opened ? (
        <Modal isOpen onOpenChange={(v) => !v && setOpened(null)}>
          <Modal.Backdrop>
            <Modal.Container placement="center">
              <Modal.Dialog aria-label={ex ? ex.title : 'How the Watchlist works'} className="sm:max-w-md" data-risk-explainer={ex ? ex.title : 'overview'}>
                <Modal.CloseTrigger />
                <Modal.Header>
                  <Modal.Heading className="text-sm">{ex ? ex.title : 'How the Watchlist works'}</Modal.Heading>
                </Modal.Header>
                <Modal.Body className="flex flex-col gap-3">
                  {ex ? (
                    <>
                      <Section heading="What it means">{ex.means}</Section>
                      <Section heading="Who does it">{ex.who}</Section>
                      <Section heading="What moves it on">{ex.movesOn}</Section>
                    </>
                  ) : (
                    <>
                      <ol className="m-0 flex list-decimal flex-col gap-1.5 ps-4 text-xs leading-relaxed text-muted">
                        {HOW_IT_WORKS.map((line) => {
                          const [head, ...rest] = line.split('. ');
                          return (
                            <li key={line}>
                              <span className="font-medium text-foreground">{head}.</span> {rest.join('. ')}
                            </li>
                          );
                        })}
                      </ol>
                      <p className="m-0 text-xs text-muted">{OWNER_AND_WORK} Press any step in the picture for more.</p>
                    </>
                  )}
                </Modal.Body>
                <Modal.Footer className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onPress={() => setOpened(null)}>
                    Close
                  </Button>
                  {ex?.showMe ? (
                    <Button
                      size="sm"
                      variant="primary"
                      isDisabled={here === 0}
                      onPress={() => {
                        onShowMe(ex.showMe!);
                        setOpened(null);
                      }}
                      data-risk-show-me
                    >
                      {here === 0 ? 'Nothing here now' : `Show me ${here === 1 ? 'the one' : `these ${here}`}`}
                    </Button>
                  ) : null}
                </Modal.Footer>
              </Modal.Dialog>
            </Modal.Container>
          </Modal.Backdrop>
        </Modal>
      ) : null}
    </section>
  );
}
